import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
  formatPercentageValue,
  formatNumberToLocale
} from '../../helpers/numberFormatter';
import * as level1DataProp from 'apex-web/lib/propTypes/level1Data';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import { instrumentPropType } from 'apex-web/lib/propTypes/instrumentPropTypes';
import InstrumentTableItem from './InstrumentTableItem';
import INSTRUMENT_TABLE_ITEMS from './InstrumentTableItems';
import { convertIncrementToIntDecimalPlaces } from 'apex-web/lib/helpers/decimalPlaces/decimalPlacesHelper';

import './InstrumentTableComponent.css';

const getProductSymbol = instrument => get(instrument, 'Product1Symbol', '');

const InstrumentTableComponent = (props, context) => {
  const { t } = context;
  const { level1, selectedInstrument, items, customClass } = props;

  const baseClasses = getBEMClasses(['instrument-table', customClass]);

  const productSymbol = get(selectedInstrument, 'Product1Symbol', '');
  const priceSymbol = get(selectedInstrument, 'Product2Symbol', '');

  const instrumentTableItems = items.map(item =>
    level1ItemsData[item.field](t, level1, selectedInstrument)
  );

  return (
    <div className={baseClasses()}>
      {(customClass === 'market-overview-instrument' ||
        customClass === 'market-overview-top-bar') && (
        <div className={baseClasses('row')}>
          <span className={baseClasses('title')}>
            {t(`${productSymbol}${priceSymbol} Price Data:`)}
          </span>
        </div>
      )}

      {instrumentTableItems.map(({ key, value, label, classModifiers }) => (
        <InstrumentTableItem
          key={key}
          value={value}
          label={label}
          baseClasses={baseClasses}
          classModifiers={classModifiers}
        />
      ))}
    </div>
  );
};

InstrumentTableComponent.propTypes = {
  level1: level1DataProp.type.isRequired,
  selectedInstrument: instrumentPropType,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired
    })
  )
};

InstrumentTableComponent.defaultProps = {
  level1: level1DataProp.defaultProps,
  items: [
    { field: INSTRUMENT_TABLE_ITEMS.lastPrice },
    { field: INSTRUMENT_TABLE_ITEMS.dailyPercentageChange },
    { field: INSTRUMENT_TABLE_ITEMS.bid },
    { field: INSTRUMENT_TABLE_ITEMS.ask },
    { field: INSTRUMENT_TABLE_ITEMS.dailyVolume },
    { field: INSTRUMENT_TABLE_ITEMS.dailyMin },
    { field: INSTRUMENT_TABLE_ITEMS.dailyMax }
  ]
};

InstrumentTableComponent.contextTypes = {
  t: PropTypes.func.isRequired
};

export default InstrumentTableComponent;

const level1ItemsData = {
  [INSTRUMENT_TABLE_ITEMS.lastPrice]: (t, { LastTradedPx }, instrument) => ({
    key: INSTRUMENT_TABLE_ITEMS.lastPrice,
    value: formatNumberToLocale(
      LastTradedPx,
      convertIncrementToIntDecimalPlaces(get(instrument, 'PriceIncrement', 0))
    ),
    label: t('Last Price'),
    classModifiers: ['last-price']
  }),

  [INSTRUMENT_TABLE_ITEMS.dailyPercentageChange]: (
    t,
    { Rolling24HrPxChangePercent },
    instrument
  ) => ({
    key: INSTRUMENT_TABLE_ITEMS.dailyPercentageChange,
    value: formatPercentageValue(Rolling24HrPxChangePercent),
    label: t('24 Hour Pct Chg'),
    classModifiers: [
      Rolling24HrPxChangePercent >= 0 ? '24h-positive' : '24h-negative'
    ]
  }),

  [INSTRUMENT_TABLE_ITEMS.bid]: (t, { BestBid }, instrument) => ({
    key: INSTRUMENT_TABLE_ITEMS.bid,
    value: formatNumberToLocale(
      BestBid,
      convertIncrementToIntDecimalPlaces(instrument.PriceIncrement)
    ),
    label: t('Bid'),
    classModifiers: ['hideable', 'bid']
  }),

  [INSTRUMENT_TABLE_ITEMS.ask]: (t, { BestOffer }, instrument) => ({
    key: INSTRUMENT_TABLE_ITEMS.ask,
    value: formatNumberToLocale(
      BestOffer,
      convertIncrementToIntDecimalPlaces(instrument.PriceIncrement)
    ),
    label: t('Ask'),
    classModifiers: ['hideable', 'ask']
  }),

  [INSTRUMENT_TABLE_ITEMS.dailyVolume]: (
    t,
    { Rolling24HrVolume },
    instrument
  ) => ({
    key: INSTRUMENT_TABLE_ITEMS.dailyVolume,
    value: `${formatNumberToLocale(
      Rolling24HrVolume,
      convertIncrementToIntDecimalPlaces(instrument.QuantityIncrement)
    )} ${getProductSymbol(instrument)}`,
    label: t('24 Hour Volume'),
    classModifiers: ['24h-volume']
  }),

  [INSTRUMENT_TABLE_ITEMS.dailyMin]: (t, { SessionLow }, instrument) => ({
    key: INSTRUMENT_TABLE_ITEMS.dailyMin,
    value: formatNumberToLocale(
      SessionLow,
      convertIncrementToIntDecimalPlaces(instrument.PriceIncrement)
    ),
    label: t('24 Hour Low'),
    classModifiers: ['hideable', '24h-low']
  }),

  [INSTRUMENT_TABLE_ITEMS.dailyMax]: (t, { SessionHigh }, instrument) => ({
    key: INSTRUMENT_TABLE_ITEMS.dailyMax,
    value: formatNumberToLocale(
      SessionHigh,
      convertIncrementToIntDecimalPlaces(instrument.PriceIncrement)
    ),
    label: t('24 Hour High'),
    classModifiers: ['hideable', '24h-high']
  })
};
